import { Button } from "design-system/atoms/Button"
import { MessagesSquare } from "lucide-react"
import { useWhatsappOpeningMessagesQuery } from "modules/campaigns/hooks/sections/openingMessages/useWhatsappOpeningMessagesQuery"
import { useTranslation } from "react-i18next"
import { ConversationFlowEnum, ConversationsResponseDataSchema, ConversationTypeEnum } from "schema"
import { z } from "zod"

type Props = {
  conversation: z.infer<typeof ConversationsResponseDataSchema>[number]
  onSelectOpeningMessage: (content: string) => void
}

export const EmptyConversation = ({ conversation, onSelectOpeningMessage }: Props) => {
  const { t } = useTranslation()
  const { data: openingMessages, isLoading } = useWhatsappOpeningMessagesQuery({
    enabled:
      !!conversation.campaign &&
      conversation.type === ConversationTypeEnum.Testing &&
      !conversation.hasMessages,
    query: {
      filterBy: {
        campaignId: conversation.campaign?.id,
      },
    },
  })

  if (conversation.hasMessages) return null
  if (isLoading) return null

  if (
    conversation.type === ConversationTypeEnum.Testing &&
    conversation.conversationFlow === ConversationFlowEnum.Inbound
  ) {
    return (
      <div className="flex flex-col gap-8 py-14 items-center justify-center text-center m-full max-w-md mx-auto">
        <MessagesSquare className="size-10 text-muted-foreground" strokeWidth={1.5} />
        <div className="flex flex-col gap-1 items-center justify-center text-center m-full">
          <h2 className="text-xl text-foreground">
            {t("conversations.chat.conversationEmpty.title")}
          </h2>
          <span className="text-sm text-muted-foreground">
            {t("conversations.chat.conversationEmpty.descriptionInbound")}
          </span>
        </div>
        {openingMessages && openingMessages.length > 0 && (
          <div className="flex flex-col gap-2 items-center justify-center">
            <span className="text-sm text-muted-foreground">
              {t("conversations.chat.conversationEmpty.openingMessages")}
            </span>
            <div className="flex flex-row gap-2 items-center justify-center flex-wrap">
              {openingMessages?.map((openingMessage) => {
                const content = openingMessage.text.replace(
                  "{{identifier}}",
                  openingMessage.identifier,
                )

                return (
                  <Button
                    key={openingMessage.id}
                    variant="outline"
                    size="sm"
                    className="w-fit"
                    onClick={() => onSelectOpeningMessage(content)}
                  >
                    <h3>{content}</h3>
                  </Button>
                )
              })}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4 py-14 items-center justify-center text-center m-full max-w-md mx-auto">
      <MessagesSquare className="size-10 text-muted-foreground" strokeWidth={1.5} />
      <div className="flex flex-col gap-1 items-center justify-center text-center m-full">
        <h2 className="text-xl text-foreground">
          {t("conversations.chat.conversationEmpty.title")}
        </h2>
        <span className="text-sm text-muted-foreground">
          {t("conversations.chat.conversationEmpty.description")}
        </span>
      </div>
    </div>
  )
}
